import { STAT_BASELINES } from '../data/scoring';
import { PLAYER_PASS_BONUS, PLAYER_RUSH_BONUS, PLAYER_REC_BONUS } from '../data/players';
import { STATS_2025 } from '../data/stats2025';

// Bonus dicts are hand-maintained with inconsistent punctuation ("A.J. Brown"
// vs "AJ Brown", "C.J. Stroud" vs "CJ Stroud"). Normalize both the dict keys
// and lookup names the same way so period/apostrophe style never causes a miss.
const normalizeBonusName = (name) => (name || "").replace(/[.'’]/g, "").toLowerCase();

function buildNormalizedBonusMap(dict) {
  const map = new Map();
  for (const [name, value] of Object.entries(dict)) {
    map.set(normalizeBonusName(name), value);
  }
  return map;
}

const NORMALIZED_PASS_BONUS = buildNormalizedBonusMap(PLAYER_PASS_BONUS);
const NORMALIZED_RUSH_BONUS = buildNormalizedBonusMap(PLAYER_RUSH_BONUS);
const NORMALIZED_REC_BONUS = buildNormalizedBonusMap(PLAYER_REC_BONUS);

export function generatePlayerStats(pos, rankNum, playerName) {
  const real = STATS_2025[playerName];
  let stats;

  if (real) {
    const g = real.g || 17;
    const n = (v) => (v == null) ? 0 : g < 17 ? Math.round(v * 17 / g) : v;

    if (pos === "K") {
      const fgm0_19 = n(real.fgm) - n(real.fgm_20_29 || 0) - n(real.fgm_30_39 || 0) - n(real.fgm_40_49 || 0) - n(real.fgm_50 || 0);
      stats = {
        fgMade: n(real.fgm),
        fgMade0_19: Math.max(0, fgm0_19),
        fgMade20_29: n(real.fgm_20_29),
        fgMade30_39: n(real.fgm_30_39),
        fgMade40_49: n(real.fgm_40_49),
        fgMade50: n(real.fgm_50),
        fgMissed: n(real.fga) - n(real.fgm),
        patMade: n(real.xpm),
        patMissed: n(real.xpa) - n(real.xpm),
      };
    } else {
      stats = {};
      // Passing
      if (real.passYds) stats.passYds = n(real.passYds);
      if (real.passTD) stats.passTD = n(real.passTD);
      if (real.passInt) stats.passInt = n(real.passInt);
      if (real.passCmp) stats.passComp = n(real.passCmp);
      if (real.passAtt) stats.passAtt = n(real.passAtt);
      if (real.pass1D) stats.pass1D = n(real.pass1D);
      if (real.sacked) stats.sacked = n(real.sacked);
      // Rushing
      if (real.rushAtt) stats.rushAtt = n(real.rushAtt);
      if (real.rushYds) stats.rushYds = n(real.rushYds);
      if (real.rushTD) stats.rushTD = n(real.rushTD);
      if (real.rush1D) stats.rush1D = n(real.rush1D);
      // Receiving
      if (real.tgt) stats.tgt = n(real.tgt);
      if (real.rec) stats.receptions = n(real.rec);
      if (real.recYds) stats.recYds = n(real.recYds);
      if (real.recTD) stats.recTD = n(real.recTD);
      if (real.rec1D) stats.rec1D = n(real.rec1D);
      // Fumbles
      if (real.fum) {
        stats.fumbles = n(real.fum);
        stats.fumblesLost = Math.round(n(real.fum) * 0.6);
      }
    }
  } else {
    // Decay-based projection for players not in STATS_2025
    const base = STAT_BASELINES[pos];
    if (!base) return {};
    const decay = (pos === "K" || pos === "DEF") ? 0.013 : 0.011;
    const scale = Math.max(0.25, 1 - decay * (rankNum - 1));
    stats = {};
    for (const [key, val] of Object.entries(base)) {
      if (key === "ptsAllowedPerGame") {
        stats[key] = Math.round((val + (rankNum - 1) * 0.3) * 10) / 10;
      } else {
        stats[key] = Math.round(val * scale);
      }
    }
  }

  // Add bonus game rates (player-specific or rank fallback)
  if (pos !== "K" && pos !== "DEF") {
    const bonus = getPlayerBonusGames(playerName, pos, rankNum);
    if (pos === "QB") {
      stats.passBonusGames = bonus.passing;
    }
    if (pos === "RB") {
      stats.rushBonusGames = bonus.rushing;
    }
    if (pos === "WR" || pos === "TE") {
      stats.recBonusGames = bonus.receiving;
    }
  }
  return stats;
}

// Rank-based fallbacks for players without historical data
function getFallbackBonusGames(pos, rankNum) {
  const r = rankNum || 1;
  let passing = 0, rushing = 0, receiving = 0;
  if (pos === "QB") {
    passing = r <= 3 ? 1.5 : r <= 8 ? 0.8 : r <= 12 ? 0.4 : 0.1;
  }
  if (pos === "RB") {
    rushing = r <= 3 ? 0.8 : r <= 8 ? 0.4 : r <= 15 ? 0.2 : 0.05;
  }
  if (pos === "WR") {
    receiving = r <= 3 ? 1.0 : r <= 8 ? 0.5 : r <= 15 ? 0.2 : 0.05;
  }
  if (pos === "TE") {
    receiving = r <= 1 ? 0.15 : r <= 3 ? 0.1 : 0;
  }
  return { passing, rushing, receiving };
}

function getPlayerBonusGames(name, pos, rankNum) {
  const fallback = getFallbackBonusGames(pos, rankNum);
  const key = normalizeBonusName(name);
  return {
    passing: NORMALIZED_PASS_BONUS.has(key) ? NORMALIZED_PASS_BONUS.get(key) : fallback.passing,
    rushing: NORMALIZED_RUSH_BONUS.has(key) ? NORMALIZED_RUSH_BONUS.get(key) : fallback.rushing,
    receiving: NORMALIZED_REC_BONUS.has(key) ? NORMALIZED_REC_BONUS.get(key) : fallback.receiving,
  };
}

export function calculateProjection(stats, pos, scoring) {
  let total = 0;
  // Passing
  if (stats.passYds) total += stats.passYds / scoring.passYdsPer;
  if (stats.passTD) total += stats.passTD * scoring.passTD;
  if (stats.passInt) total += stats.passInt * scoring.passInt;
  if (stats.passComp && scoring.passComp) total += stats.passComp * scoring.passComp;
  // Rushing
  if (stats.rushYds) total += stats.rushYds / scoring.rushYdsPer;
  if (stats.rushTD) total += stats.rushTD * scoring.rushTD;
  // Receiving
  if (stats.receptions) total += stats.receptions * scoring.receptions;
  if (stats.receptions && scoring.recBonusRB && pos === "RB") total += stats.receptions * scoring.recBonusRB;
  if (stats.receptions && scoring.recBonusWR && pos === "WR") total += stats.receptions * scoring.recBonusWR;
  if (stats.receptions && scoring.recBonusTE && pos === "TE") total += stats.receptions * scoring.recBonusTE;
  if (stats.recYds) total += stats.recYds / scoring.recYdsPer;
  if (stats.recTD) total += stats.recTD * scoring.recTD;
  // Bonus games
  if (stats.passBonusGames && scoring.passYdsBonus) total += stats.passBonusGames * scoring.passYdsBonus;
  if (stats.rushBonusGames && scoring.rushYdsBonus) total += stats.rushBonusGames * scoring.rushYdsBonus;
  if (stats.recBonusGames && scoring.recYdsBonus) total += stats.recBonusGames * scoring.recYdsBonus;
  // Misc
  // Convention B: `fumbles` weight applies to NON-lost fumbles only; lost
  // fumbles use `fumblesLost`. fum totals include lost — no double-count.
  {
    const lost = stats.fumblesLost || 0;
    const kept = Math.max(0, (stats.fumbles || 0) - lost);
    if (kept) total += kept * scoring.fumbles;
    if (lost) total += lost * scoring.fumblesLost;
  }
  // Kicker
  if (stats.fgMade0_19 || stats.fgMade20_29 || stats.fgMade30_39 || stats.fgMade40_49 || stats.fgMade50) {
    if (stats.fgMade0_19) total += stats.fgMade0_19 * (scoring.fgMade0_19 || scoring.fgMade);
    if (stats.fgMade20_29) total += stats.fgMade20_29 * (scoring.fgMade20_29 || scoring.fgMade);
    if (stats.fgMade30_39) total += stats.fgMade30_39 * (scoring.fgMade30_39 || scoring.fgMade);
    if (stats.fgMade40_49) total += stats.fgMade40_49 * (scoring.fgMade40_49 || scoring.fgMade);
    if (stats.fgMade50) total += stats.fgMade50 * (scoring.fgMade50 || scoring.fgMade);
  } else if (stats.fgMade) {
    total += stats.fgMade * scoring.fgMade;
  }
  if (stats.fgMissed) total += stats.fgMissed * scoring.fgMissed;
  if (stats.patMade) total += stats.patMade * scoring.patMade;
  if (stats.patMissed) total += stats.patMissed * scoring.patMissed;
  // Defense
  if (stats.sacks) total += stats.sacks * scoring.defSack;
  if (stats.ints) total += stats.ints * scoring.defInt;
  if (stats.fumRec) total += stats.fumRec * scoring.defFumRec;
  if (stats.defTD) total += stats.defTD * scoring.defTD;
  if (stats.safeties) total += stats.safeties * scoring.defSafety;
  if (stats.blockedKicks) total += stats.blockedKicks * scoring.defBlockKick;
  if (stats.ptsAllowedPerGame !== undefined) {
    const ppg = stats.ptsAllowedPerGame;
    const bracket = ppg <= 0 ? scoring.defPtsAllowed0 : ppg <= 6 ? scoring.defPtsAllowed1_6
      : ppg <= 13 ? scoring.defPtsAllowed7_13 : ppg <= 20 ? scoring.defPtsAllowed14_20
      : ppg <= 27 ? scoring.defPtsAllowed21_27 : ppg <= 34 ? scoring.defPtsAllowed28_34
      : scoring.defPtsAllowed35;
    total += bracket * 17;
  }
  return Math.round(total);
}

export function getActual2025PPG(playerName, pos, scoring) {
  const real = STATS_2025[playerName];
  if (!real) return null;
  const g = real.g || 17;
  let stats;

  if (pos === "K") {
    const fgm0_19 = (real.fgm || 0) - (real.fgm_20_29 || 0) - (real.fgm_30_39 || 0) - (real.fgm_40_49 || 0) - (real.fgm_50 || 0);
    stats = {
      fgMade: real.fgm || 0,
      fgMade0_19: Math.max(0, fgm0_19),
      fgMade20_29: real.fgm_20_29 || 0,
      fgMade30_39: real.fgm_30_39 || 0,
      fgMade40_49: real.fgm_40_49 || 0,
      fgMade50: real.fgm_50 || 0,
      fgMissed: (real.fga || 0) - (real.fgm || 0),
      patMade: real.xpm || 0,
      patMissed: (real.xpa || 0) - (real.xpm || 0),
    };
  } else {
    stats = {};
    if (real.passYds) stats.passYds = real.passYds;
    if (real.passTD) stats.passTD = real.passTD;
    if (real.passInt) stats.passInt = real.passInt;
    if (real.passCmp) stats.passComp = real.passCmp;
    if (real.passAtt) stats.passAtt = real.passAtt;
    if (real.pass1D) stats.pass1D = real.pass1D;
    if (real.sacked) stats.sacked = real.sacked;
    if (real.rushAtt) stats.rushAtt = real.rushAtt;
    if (real.rushYds) stats.rushYds = real.rushYds;
    if (real.rushTD) stats.rushTD = real.rushTD;
    if (real.rush1D) stats.rush1D = real.rush1D;
    if (real.tgt) stats.tgt = real.tgt;
    if (real.rec) stats.receptions = real.rec;
    if (real.recYds) stats.recYds = real.recYds;
    if (real.recTD) stats.recTD = real.recTD;
    if (real.rec1D) stats.rec1D = real.rec1D;
    if (real.fum) {
      stats.fumbles = real.fum;
      stats.fumblesLost = Math.round(real.fum * 0.6);
    }
  }

  const total = calculateProjection(stats, pos, scoring);
  return Math.round((total / g) * 10) / 10;
}

function getProjectedPPG(player, scoring) {
  const stats = generatePlayerStats(player.pos, player.rank, player.name);
  const total = calculateProjection(stats, player.pos, scoring);
  return Math.round((total / 17) * 10) / 10;
}
